import { AfterViewInit, Component, ElementRef, ViewChild, inject } from '@angular/core'
import {
  AbstractControl,
  AbstractControlOptions,
  FormBuilder,
  ReactiveFormsModule,
  Validators
} from '@angular/forms'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { Tab } from 'bootstrap'
import { passwordPatternValidator } from '../../../../validators/validators'
import { AuthService, StorageDocument } from '../../../../services/auth.service'
import { commonUtil } from '../../../../utils/common.util'
import { Router } from '@angular/router'
import { ApiRegisterUser } from '../../../../modal/api.modal'
import { Locale } from '../../../../services/language.service'

interface TimeText {
  en: string
  zh: string
}

interface TimeSlot {
  day: string
  time: TimeText
}

interface LocationPreference {
  id: number
  name: string
  address: string
  phone: string
  timeSlots: TimeSlot[]
}

@Component({
  selector: 'app-auth-register',
  imports: [TranslatePipe, ReactiveFormsModule],
  templateUrl: './register.component.html',
  styleUrl: './register.component.css'
})
export class AuthRegister implements AfterViewInit {
  private formBuilder = inject(FormBuilder)
  private router = inject(Router)
  private tranService = inject(TranslateService)
  private authService = inject(AuthService)
  tabs: Tab[] = []
  currentTabIndex = 0
  locationPreferences: LocationPreference[] = [
    {
      id: 1,
      name: 'app.register.tuenMunPublicRidingSchool',
      address: 'app.register.tuenMunPublicRidingSchoolAddress',
      phone: '2454 9961',
      timeSlots: [
        { day: 'app.register.monToSat', time: { en: 'AM 9:00 - 11:30', zh: '上午 9:00 - 11:30' } },
        { day: 'app.register.monToWedFri', time: { en: '2:00 PM – 4:30 PM', zh: '下午 2:00 – 4:30' } }
      ]
    },
    {
      id: 4,
      name: 'app.register.pokfulamPublicRidingSchool',
      address: 'app.register.pokfulamPublicRidingSchoolAddress',
      phone: '2875 7711',
      timeSlots: [
        { day: 'app.register.monToFri', time: { en: '9:30 AM – 12:00 Noon', zh: '上午 9:30 – 中午12:00' } },
        { day: 'app.register.sat', time: { en: '9:00 AM – 11:00 AM', zh: '上午 9:00 - 11:00' } },
        { day: 'app.register.wedAndFri', time: { en: '2:30 PM – 5:00 PM', zh: '下午 2:30 – 5:00' } }
      ]
    },
    {
      id: 3,
      name: 'app.register.leiYueMunPublicRidingSchool',
      address: 'app.register.leiYueMunPublicRidingSchoolAddress',
      phone: '2454 9961',
      timeSlots: [
        { day: 'app.register.tueAndThu', time: { en: 'AM 08:30 - 11:00', zh: '上午 08:30 - 11:00' } }
      ]
    }
  ]
  knowAndExpOptions = [
    {
      id: 1,
      name: 'Photography/Video Making',
      translate: 'app.register.knowAndExp.pvMaking'
    },
    {
      id: 2,
      name: 'Working with Children',
      translate: 'app.register.knowAndExp.workWithChild'
    },
    {
      id: 3,
      name: 'Working with people with disabilities',
      translate: 'app.register.knowAndExp.workWithPD'
    },
    {
      id: 4,
      name: 'Media',
      translate: 'app.register.knowAndExp.media'
    }
  ]
  howFindOptions = [
    {
      id: 1,
      name: 'RDA HK Website',
      translate: 'app.register.howDidFind.rdaHKWebsite'
    },
    {
      id: 2,
      name: 'RDA Facebook',
      translate: 'app.register.howDidFind.rdaFacebook'
    },
    {
      id: 3,
      name: 'Search Engine',
      translate: 'app.register.howDidFind.searchEngine'
    },
    {
      id: 4,
      name: 'Friends',
      translate: 'app.register.howDidFind.friends'
    }
  ]
  personalInfo = this.formBuilder.group(
    {
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, passwordPatternValidator()]],
      confPassword: ['', [Validators.required]],
      cnName: ['', [Validators.required]],
      enName: ['', [Validators.required]],
      birth: ['', [Validators.required, (control: AbstractControl) => {
        if (!control.value) return null
        const dob = new Date(control.value)
        const today = new Date()
        let age = today.getFullYear() - dob.getFullYear()
        const m = today.getMonth() - dob.getMonth()
        if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
          age--
        }
        return age < 18 ? { minAge: true } : null
      }]],
      idCardNumber: ['', [Validators.required]],
      gender: ['', [Validators.required]],
      address: ['', [Validators.required]],
      occupation: ['', [Validators.required]],
      phoneCode: ['+852'],
      phone: ['', [Validators.required, Validators.pattern('[0-9]{8,}')]],
      showPwd: [''],
      showConfPwd: ['']
    },
    {
      validators: [
        (control) => {
          let val = control.value
          if (val.password !== val.confPassword) {
            return { passwordNotMatch: true }
          }
          return null
        }
      ]
    } as AbstractControlOptions
  )
  emergencyContact = this.formBuilder.group({
    name: ['', [Validators.required]],
    relationship: ['', [Validators.required]],
    phoneCode: ['+852'],
    phone: ['', [Validators.required, Validators.pattern('[0-9]{8,}')]]
  })
  locPreference = this.formBuilder.group({
    ids: ['', [Validators.requiredTrue]]
  })
  knowAndExp = this.formBuilder.group(
    {
      expRfdRadio: ['', [Validators.required]],
      expRfdRemark: [''],
      expRhRadio: ['', [Validators.required]],
      expRhRemark: [''],
      expMedRadio: ['', [Validators.required]],
      expMedRemark: [''],
      knowAndExpCheckBox: ['', Validators.requiredTrue],
      howFindCheckBox: [''],
      howFindOtherCheckBox: [''],
      howFindRemark: ['']
    },
    {
      validators: [
        (control) => {
          let val = control.value
          let errors = {} as any
          if (val.expRfdRadio === 'yes' && !val.expRfdRemark) {
            errors.expRfdRemarkRequired = true
          }
          if (val.expMedRadio === 'yes' && !val.expMedRemark) {
            errors.expMedRemarkRequired = true
          }
          if (val.expRhRadio === 'yes' && !val.expRhRemark) {
            errors.expRhRemarkRequired = true
          }
          if (val.howFindOtherCheckBox && !val.howFindRemark) {
            errors.howFindRemarkRequired = true
          }
          if (!(val.howFindCheckBox || val.howFindOtherCheckBox)) {
            errors.howFindRequired = true
          }
          return errors
        }
      ]
    } as AbstractControlOptions
  )
  volReferral = this.formBuilder.group({
    fullName: [''],
    phoneCode: ['+852'],
    contactNumber: ['', [Validators.pattern('[0-9]{8,}')]],
    relationship: ['']
  })
  declAndCons = this.formBuilder.group({
    declaration1: ['', [Validators.requiredTrue]],
    declaration2: ['', [Validators.requiredTrue]],
    photoConsent: ['', [Validators.required]],
    consentCVASP: ['', [Validators.requiredTrue]],
    consentRaR: ['', [Validators.requiredTrue]],
    consentVWG: ['', [Validators.requiredTrue]],
    consentIF2016: ['', [Validators.requiredTrue]],
    agreeToPrivacy: [false, [Validators.requiredTrue]]
  })
  locPrefIds = new Set<number>()
  knowAndExpIds = new Set<number>()
  howDidFindIds = new Set<number>()
  consentDocs = {
    cvasp: {} as StorageDocument,
    rr: {} as StorageDocument,
    vwg: {} as StorageDocument,
    if2016: {} as StorageDocument
  }
  isRegistering = false
  locale: Locale = Locale.DEFAULT

  get siteTncTitle(): string {
    if (!this.siteTncDoc) return ''
    const lang = this.tranService.currentLang
    if (lang === Locale.ZH_CN) return this.siteTncDoc.name.cn
    if (lang === Locale.ZH_HK) return this.siteTncDoc.name.hk
    return this.siteTncDoc.name.en
  }

  showTermsModal = false
  isScrolledToBottom = false
  loadingPdf = false
  siteTncDoc: StorageDocument | null = null

  @ViewChild('modalBody') modalBody!: ElementRef
  @ViewChild('termsContainer') termsContainer!: ElementRef

  private pdfjsLib: any = null

  timeText(time: TimeText): string {
    const lang = this.tranService.currentLang
    if (lang === 'zh-CN' || lang === 'zh-HK') return time.zh
    return time.en
  }

  pickLocPreference(event: any, id: number) {
    if (event.target?.checked) {
      this.locPrefIds.add(id)
    } else {
      this.locPrefIds.delete(id)
    }
  }

  pickKnowAndExp(event: any, id: number) {
    if (event.target?.checked) {
      this.knowAndExpIds.add(id)
    } else {
      this.knowAndExpIds.delete(id)
    }
  }

  pickHowDidFind(event: any, id: number) {
    if (event.target?.checked) {
      this.howDidFindIds.add(id)
    } else {
      this.howDidFindIds.delete(id)
    }
  }

  isFormValid() {
    return (
      this.personalInfo.valid &&
      this.emergencyContact.valid &&
      this.locPreference.valid &&
      this.knowAndExp.valid &&
      this.volReferral.valid &&
      this.declAndCons.valid
    )
  }

  currentPartForm() {
    return [
      this.personalInfo,
      this.emergencyContact,
      this.locPreference,
      this.knowAndExp,
      this.volReferral,
      this.declAndCons
    ][this.currentTabIndex]
  }

  nextPart() {
    this.tabs[++this.currentTabIndex].show()
  }

  prevPart() {
    this.tabs[--this.currentTabIndex].show()
  }

  preventDefault(event: Event) {
    event.preventDefault()
  }

  shouldTabShow(tabIndex: number) {
    if (tabIndex === 0) {
      return true
    }
    if (tabIndex === 1) {
      return this.personalInfo.valid
    }
    if (tabIndex === 2) {
      return this.emergencyContact.valid
    }
    if (tabIndex === 3) {
      return this.locPreference.valid
    }
    if (tabIndex === 4 || tabIndex === 5) {
      return this.knowAndExp.valid
    }

    return false
  }

  onTabShow(event: any) {
    let elIndex = event.target?.getAttribute('data-rda-index')
    if (!elIndex) {
      return
    }
    elIndex = Number.parseInt(elIndex)
    if (this.isRegistering || !this.shouldTabShow(elIndex)) {
      this.currentPartForm().markAllAsTouched()
      event.preventDefault()
      return
    }
    this.currentTabIndex = elIndex
  }

  doRegister() {
    if (!this.isFormValid()) {
      return
    }
    this.isRegistering = true
    let personInfoVal = this.personalInfo.value
    let emergencyContactVal = this.emergencyContact.value
    let knowAndExpVal = this.knowAndExp.value
    let volReferralVal = this.volReferral.value
    let howDidFindString = [...this.howDidFindIds]
      .map((id) => {
        return this.howFindOptions[id - 1].name
      })
      .join(',')
    if (knowAndExpVal.howFindOtherCheckBox) {
      if (howDidFindString) {
        howDidFindString += ',Others'
      } else {
        howDidFindString = 'Others'
      }
    }
    this.authService
      .register({
        name_en: personInfoVal.enName!,
        name_zh_cn: personInfoVal.cnName!,
        name_zh_hk: personInfoVal.cnName!,
        birth: personInfoVal.birth!,
        id_number: personInfoVal.idCardNumber!,
        gender: personInfoVal.gender! === 'male' ? 'm' : 'f',
        occupation: personInfoVal.occupation!,
        address: personInfoVal.address!,
        mobile: (personInfoVal.phoneCode || '+852') + personInfoVal.phone!,
        email: personInfoVal.email!,
        password: personInfoVal.password!,
        password_confirmation: personInfoVal.confPassword!,
        emergency_name: emergencyContactVal.name!,
        emergency_mobile: (emergencyContactVal.phoneCode || '+852') + emergencyContactVal.phone!,
        emergency_relationship: emergencyContactVal.relationship!,
        prefer_venues: [...this.locPrefIds].sort().join(','),
        experience_with_riding_for_disabled:
          knowAndExpVal.expRfdRadio === 'yes',
        experience_with_riding_for_disabled_detail: knowAndExpVal.expRfdRemark!,
        riding_experience: knowAndExpVal.expRhRadio === 'yes',
        riding_experience_detail: knowAndExpVal.expRhRemark,
        medical_experience: knowAndExpVal.expMedRadio === 'yes',
        medical_experience_detail: knowAndExpVal.expMedRemark,
        photography: this.knowAndExpIds.has(1),
        working_with_children: this.knowAndExpIds.has(2),
        working_with_disabilities: this.knowAndExpIds.has(3),
        media: this.knowAndExpIds.has(4),
        how_to_find_rda: howDidFindString,
        how_to_find_rda_other: knowAndExpVal.howFindRemark,
        referrer_name: volReferralVal.fullName!,
        referrer_mobile: volReferralVal.contactNumber ? (volReferralVal.phoneCode || '+852') + volReferralVal.contactNumber : '',
        referrer_relationship: volReferralVal.relationship!,
        agree_volunteer_risk: true,
        agree_volunteer_guide: true,
        agree_safeguarding_guidelines: true,
        agree_rules_regulation: true,
        agree_safeguarding_policy: true,
        agree_indemnity_form: true,
        agree_photo_use_rider: this.declAndCons.value.photoConsent === 'agree',
        service_hour: 0
      } as ApiRegisterUser)
      .subscribe({
        next: (success) => {
          this.isRegistering = false
          if (success) {
            commonUtil.alert(
              this.tranService.instant('app.register.registerSuccessfully'),
              'success'
            )
            this.router.navigate(['auth', 'login'])
          }
        },
        error: () => {
          this.isRegistering = false
        }
      })
  }

  private async getPdfJs(): Promise<any> {
    if (this.pdfjsLib) return this.pdfjsLib
    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js'
      script.onload = () => {
        const lib = (window as any).pdfjsLib
        lib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'
        this.pdfjsLib = lib
        resolve(lib)
      }
      script.onerror = () => reject(new Error('Failed to load pdf.js'))
      document.head.appendChild(script)
    })
  }

  private async renderPdfToImages(url: string, containerWidth: number): Promise<HTMLImageElement[]> {
    const lib = await this.getPdfJs()
    const pdf = await lib.getDocument(url).promise
    const images: HTMLImageElement[] = []
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i)
      const base = page.getViewport({ scale: 1 })
      const scale = Math.min(1.5, (containerWidth - 4) / base.width)
      const viewport = page.getViewport({ scale })
      const canvas = document.createElement('canvas')
      canvas.width = viewport.width
      canvas.height = viewport.height
      const ctx = canvas.getContext('2d')!
      await page.render({ canvasContext: ctx, viewport }).promise
      const img = document.createElement('img')
      img.className = 'pdf-page'
      img.src = canvas.toDataURL('image/png')
      img.alt = `Page ${i}`
      images.push(img)
    }
    return images
  }

  onAgreePrivacyClick(event: Event): void {
    if (!this.declAndCons.get('agreeToPrivacy')?.value) {
      event.preventDefault()
      this.openTermsModal()
    }
  }

  async openTermsModal(): Promise<void> {
    this.isScrolledToBottom = false
    this.showTermsModal = true
    this.loadingPdf = true
    this.lockBodyScroll()
    await new Promise(r => setTimeout(r, 150))
    try {
      if (!this.siteTncDoc?.url) return
      const containerEl = this.termsContainer?.nativeElement
      const cw = containerEl ? containerEl.clientWidth - 4 : 800
      const pages = await this.renderPdfToImages(this.siteTncDoc.url, cw)
      const tc = this.termsContainer?.nativeElement
      if (tc) { tc.innerHTML = ''; pages.forEach(c => tc.appendChild(c)) }
    } catch {
      console.error('PDF render failed')
    }
    this.loadingPdf = false
  }

  acceptTerms(): void {
    this.declAndCons.get('agreeToPrivacy')?.setValue(true)
    this.declAndCons.get('agreeToPrivacy')?.markAsDirty()
    this.showTermsModal = false
    this.isScrolledToBottom = false
    this.unlockBodyScroll()
  }

  declineTerms(): void {
    this.declAndCons.get('agreeToPrivacy')?.setValue(false)
    this.showTermsModal = false
    this.isScrolledToBottom = false
    this.unlockBodyScroll()
  }

  onModalScroll(event: Event): void {
    const element = event.target as HTMLElement
    const threshold = 5
    this.isScrolledToBottom = element.scrollHeight - element.scrollTop - element.clientHeight < threshold
  }

  private lockBodyScroll(): void {
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.width = '100%'
  }

  private unlockBodyScroll(): void {
    document.body.style.overflow = ''
    document.body.style.position = ''
    document.body.style.width = ''
  }

  ngAfterViewInit(): void {
    this.authService.getConsentDocs().subscribe((docs) => {
      docs?.forEach((doc) => {
        switch (doc.type) {
          case 'agree_safeguarding_policy':
            this.consentDocs.cvasp = doc
            break
          case 'agree_rules_regulation':
            this.consentDocs.rr = doc
            break
          case 'agree_volunteer_guide':
            this.consentDocs.vwg = doc
            break
          case 'agree_indemnity_form':
            this.consentDocs.if2016 = doc
            break
          case 'site_tnc_privacy':
            this.siteTncDoc = doc
            break
        }
      })
      let { cvasp, rr, vwg, if2016 } = this.consentDocs
      this.tranService.setTranslation(
        Locale.EN,
        {
          consentCVASP: cvasp.name.en,
          consentRaR: rr.name.en,
          consentVWG: vwg.name.en,
          consentIF2016: if2016.name.en
        },
        true
      )
      this.tranService.setTranslation(
        Locale.ZH_CN,
        {
          consentCVASP: cvasp.name.cn,
          consentRaR: rr.name.cn,
          consentVWG: vwg.name.cn,
          consentIF2016: if2016.name.cn
        },
        true
      )
      this.tranService.setTranslation(
        Locale.ZH_HK,
        {
          consentCVASP: cvasp.name.hk,
          consentRaR: rr.name.hk,
          consentVWG: vwg.name.hk,
          consentIF2016: if2016.name.hk
        },
        true
      )
    })
    let tabId = '#tabPart'
    for (let i = 1; i <= 6; ++i) {
      this.tabs.push(Tab.getOrCreateInstance(tabId + i))
    }
  }
}
